import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import DraggableDialog from 'components/common/DraggableDialog';
import { FormArea } from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import CodeLookupDialog from 'components/budget/CodeLookupDialog';
import { ActionButton, CommonCss } from 'styles/common.styles';
import { theme, SEARCH_LABEL_W } from 'styles/theme';
import { showAlert } from 'utils/toastBridge';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import {
  MONTHLY_BUDGET_LABEL,
  MONTH_STACK_COLUMNS,
  buildMonthlyRowsFromMasters,
  distributeMonthly,
  emptyMonthAmounts,
  enrichMonthlyRow,
  loadMonthlyBudgets,
  saveMonthlyBudgets,
  withMonthlyGridIds,
} from 'budget/budgetMonthlyBudgetStore';
import { filterExpenseRequests } from 'budget/budgetExpenseRequestStore';
import { filterIncomeRequests } from 'budget/budgetIncomeRequestStore';
import { acctClassOptions, acctClassLabel } from 'budget/budgetAcctStore';
import { loadDepts, filterDepts } from 'master/deptStore';
import { loadOrSeedProjects } from 'budget/budgetProjectStore';
import {
  loadSubjectsByFilter,
} from 'budget/budgetStructureStore';
import {
  buildSubjectDispCode,
  buildSubjectDispName,
} from 'budget/budgetSubjectHistoryStore';
import { loadFunds, loadFundPackages } from 'budget/budgetFundStore';

const GRID_ID = 'budget-monthly-budget';
const AMT_SIZE = 96;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;
  min-height: 0;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const Hint = styled.div`
  font-size: 0.75rem;
  color: ${theme.colors.text?.secondary || '#6b7280'};
  flex: 1;
  min-width: 180px;
`;

const SearchWrap = styled.div`
  flex-shrink: 0;
  padding: 4px 0 0;
  border-top: 1px solid ${theme.colors.border || '#e5e7eb'};
`;

const GridHost = styled.div`
  flex: 1;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  border: 1px solid ${theme.colors.border || '#e5e7eb'};
  border-radius: 4px;
  overflow: hidden;
`;

const FooterBar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  flex-shrink: 0;
`;

/** 미저장 닫기 확인 (저장 후 닫기 / 닫기 / 취소) */
const CloseConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10001;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
`;

const CloseConfirmBox = styled.div`
  min-width: 400px;
  max-width: 520px;
  background: #fff;
  border-radius: 8px;
  border: 3px solid #f59e0b;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  padding: 24px;
`;

const CloseConfirmMsg = styled.div`
  color: #333;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  margin-bottom: 24px;
  white-space: pre-line;
`;

const CloseConfirmBtns = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
`;

function formatCodeName(code, name) {
  const c = String(code || '').trim();
  const n = String(name || '').trim();
  if (c && n) return `${c} · ${n}`;
  return c || n || '';
}

function formatMultiCodeName(items) {
  const list = (items || [])
    .map((it) => formatCodeName(it.code, it.name))
    .filter(Boolean);
  if (!list.length) return '';
  if (list.length <= 2) return list.join(', ');
  return `${list.slice(0, 2).join(', ')} 외 ${list.length - 2}건`;
}

function joinCodes(values) {
  return (values || [])
    .map((v) => String(v || '').trim())
    .filter(Boolean)
    .join(',');
}

function emptySearchForm(partial = {}) {
  return {
    acctClass: '',
    deptCode: '',
    deptName: '',
    deptDisp: '',
    projectCode: '',
    projectName: '',
    projectDisp: '',
    subjectCode: '',
    subjectName: '',
    subjectDisp: '',
    fundUid: '',
    fundPkgUid: '',
    fundCode: '',
    fundName: '',
    fundDisp: '',
    ...partial,
  };
}

/**
 * 월별예산 팝업 — 편성요구와 동일 검색조건(회계구분·부서·사업·과목·재원)으로 필터
 */
export default function MonthlyBudgetDialog({
  open,
  onClose,
  dims,
  masterRows = [],
  /** 편성요구 F7 검색조건 스냅샷 (열 때 동일 데이터 표시) */
  initialSearch = null,
  readOnly = false,
  prefsUserId: prefsUserIdProp,
}) {
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = prefsUserIdProp || buildPrefsUserId(tenantCode, user) || '';

  const gridRef = useRef(null);
  const gridHostRef = useRef(null);
  const allRowsRef = useRef([]);
  const [warnMsg, setWarnMsg] = useState('');
  const [viewCount, setViewCount] = useState(0);
  const [session, setSession] = useState(null);
  const [lookup, setLookup] = useState({ open: false, type: null });
  /** Esc/닫기 시 미저장 확인 */
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);

  const classOptions = useMemo(
    () => acctClassOptions(tenantCode || dims?.tenantCode),
    [tenantCode, dims?.tenantCode]
  );

  const searchDefaults = useMemo(
    () =>
      emptySearchForm({
        acctClass: initialSearch?.acctClass || dims?.acctClass || '',
        deptCode: initialSearch?.deptCode || '',
        deptName: initialSearch?.deptName || '',
        deptDisp: initialSearch?.deptDisp || '',
        projectCode: initialSearch?.projectCode || '',
        projectName: initialSearch?.projectName || '',
        projectDisp: initialSearch?.projectDisp || '',
        subjectCode: initialSearch?.subjectCode || '',
        subjectName: initialSearch?.subjectName || '',
        subjectDisp: initialSearch?.subjectDisp || '',
        fundUid: initialSearch?.fundUid || '',
        fundPkgUid: initialSearch?.fundPkgUid || '',
        fundCode: initialSearch?.fundCode || '',
        fundName: initialSearch?.fundName || '',
        fundDisp: initialSearch?.fundDisp || '',
      }),
    [initialSearch, dims?.acctClass]
  );

  const methods = useForm({ mode: 'onSubmit', defaultValues: searchDefaults });
  const { getValues, setValue, reset } = methods;

  const grid = useHiGridHook({
    gridRef,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: false,
  });

  const refreshWarn = useCallback((list) => {
    const unallocCnt = (list || []).filter((r) => enrichMonthlyRow(r).hasUnalloc).length;
    setWarnMsg(
      unallocCnt
        ? `미배분액 ≠ 0 인 행 ${unallocCnt}건 (미배분 열). 제출 시에는 미배분 0이 필요합니다.`
        : ''
    );
  }, []);

  /** 그리드 편집분을 전체 행 캐시에 병합 (필터 저장 시 타 행 유실 방지) */
  const syncGridIntoAllRows = useCallback(() => {
    const gridData = gridRef.current?.getData?.() || [];
    if (!gridData.length) return allRowsRef.current || [];
    const byId = new Map(
      gridData.map((r) => [String(r._rowUid || r.requestUid || r.id || ''), r])
    );
    const next = (allRowsRef.current || []).map((r) => {
      const id = String(r._rowUid || r.requestUid || r.id || '');
      const g = byId.get(id);
      return g ? { ...g } : r;
    });
    allRowsRef.current = next;
    return next;
  }, []);

  const filterFromForm = useCallback(() => {
    const v = getValues();
    return {
      deptCode: String(v.deptCode || '').trim(),
      projectCode: String(v.projectCode || '').trim(),
      subjectCode: String(v.subjectCode || '').trim(),
      fundUid: String(v.fundUid || '').trim(),
      fundPkgUid: String(v.fundPkgUid || '').trim(),
      fundCode: String(v.fundCode || '').trim(),
    };
  }, [getValues]);

  const applyViewFilter = useCallback(
    (allRows, filterQ) => {
      const q = filterQ || filterFromForm();
      const inoutDiv = String(session?.dims?.inoutDiv || dims?.inoutDiv || '지출').trim();
      const rowFilter = inoutDiv === '수입' ? filterIncomeRequests : filterExpenseRequests;
      // 회계구분은 마스터 로드 단계에서 이미 걸러진 경우가 많음 — 행에 값이 있으면 추가 필터
      let list = rowFilter(allRows || [], q);
      const acct = String(getValues('acctClass') || '').trim();
      if (acct) {
        list = list.filter((r) => {
          const rowAcct = String(r.acctClassCode || r.acctClass || '').trim();
          return !rowAcct || rowAcct === acct;
        });
      }
      return list;
    },
    [filterFromForm, getValues, session?.dims?.inoutDiv, dims?.inoutDiv]
  );

  const pushViewToGrid = useCallback(
    (allRows, filterQ) => {
      const view = withMonthlyGridIds(applyViewFilter(allRows, filterQ));
      gridRef.current?.setData?.(view);
      setViewCount(view.length);
      refreshWarn(view);
      return view;
    },
    [applyViewFilter, refreshWarn]
  );

  useEffect(() => {
    if (!open) {
      setSession(null);
      allRowsRef.current = [];
      setCloseConfirmOpen(false);
      return undefined;
    }
    const snapDims = { ...(dims || {}) };
    const formInit = emptySearchForm({
      acctClass: initialSearch?.acctClass || snapDims.acctClass || '',
      deptCode: initialSearch?.deptCode || '',
      deptName: initialSearch?.deptName || '',
      deptDisp:
        initialSearch?.deptDisp ||
        formatCodeName(initialSearch?.deptCode, initialSearch?.deptName),
      projectCode: initialSearch?.projectCode || '',
      projectName: initialSearch?.projectName || '',
      projectDisp:
        initialSearch?.projectDisp ||
        formatCodeName(initialSearch?.projectCode, initialSearch?.projectName),
      subjectCode: initialSearch?.subjectCode || '',
      subjectName: initialSearch?.subjectName || '',
      subjectDisp:
        initialSearch?.subjectDisp ||
        formatCodeName(initialSearch?.subjectCode, initialSearch?.subjectName),
      fundUid: initialSearch?.fundUid || '',
      fundPkgUid: initialSearch?.fundPkgUid || '',
      fundCode: initialSearch?.fundCode || '',
      fundName: initialSearch?.fundName || '',
      fundDisp:
        initialSearch?.fundDisp ||
        formatCodeName(initialSearch?.fundCode, initialSearch?.fundName),
    });
    reset(formInit);

    let cancelled = false;
    (async () => {
      const saved = await loadMonthlyBudgets(snapDims);
      if (cancelled) return;
      const merged = withMonthlyGridIds(
        buildMonthlyRowsFromMasters(masterRows || [], saved).map((r) => ({
          ...r,
          _sel: 'N',
          mod: '',
        }))
      );
      allRowsRef.current = merged;
      setSession({ dims: snapDims, filter: formInit });

      const filterQ = {
        deptCode: formInit.deptCode,
        projectCode: formInit.projectCode,
        subjectCode: formInit.subjectCode,
        fundUid: formInit.fundUid,
        fundPkgUid: formInit.fundPkgUid,
        fundCode: formInit.fundCode,
      };
      setTimeout(() => {
        if (!cancelled) pushViewToGrid(merged, filterQ);
      }, 0);
    })();
    return () => {
      cancelled = true;
    };
    // open 전환 시에만 로드
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const canEdit = !readOnly;

  const handleSearchFilter = useCallback(() => {
    syncGridIntoAllRows();
    const view = pushViewToGrid(allRowsRef.current);
    const f = filterFromForm();
    const parts = [];
    if (f.deptCode) parts.push('부서');
    if (f.projectCode) parts.push('사업');
    if (f.subjectCode) parts.push('과목');
    if (f.fundUid || f.fundPkgUid || f.fundCode) parts.push('재원');
    showAlert(
      `월별예산 ${view.length}건 표시 (전체 ${allRowsRef.current.length}건)${
        parts.length ? ` · 조건(${parts.join('·')})` : ' · 조건없음'
      }`,
      'success'
    );
  }, [syncGridIntoAllRows, pushViewToGrid, filterFromForm]);

  const columns = useMemo(
    () => {
      const selCol = hiCol.edCheckboxColumn({
        header: '선택',
        field: '_sel',
        size: 56,
        editable: () => canEdit,
      });
      return [
        {
          ...selCol,
          enableSorting: false,
          enableColumnFilter: false,
          // 헤더 체크 → 화면(필터) 기준 전체 선택/해제
          header: ({ table }) => {
            if (!canEdit) return '선택';
            const rows =
              table.getFilteredRowModel?.()?.rows || table.getRowModel()?.rows || [];
            const isY = (r) => String(r?.original?._sel || '').toUpperCase() === 'Y';
            const allChecked = rows.length > 0 && rows.every(isY);
            const someChecked = !allChecked && rows.some(isY);
            return React.createElement('input', {
              type: 'checkbox',
              checked: allChecked,
              disabled: rows.length === 0,
              ref: (el) => {
                if (el) el.indeterminate = someChecked;
              },
              title: '전체 선택/해제',
              'aria-label': '전체 선택/해제',
              onMouseDown: (ev) => ev.stopPropagation(),
              onClick: (ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                if (!rows.length) return;
                const next = allChecked ? 'N' : 'Y';
                // 필터 행 original 참조로 매칭 (id/_rowUid 불일치로 선택 누락·오적용 방지)
                const visible = new Set(rows.map((r) => r.original).filter(Boolean));
                gridRef.current?.setData?.((prev) =>
                  (prev || []).map((r) => (visible.has(r) ? { ...r, _sel: next } : r))
                );
              },
              style: { cursor: rows.length ? 'pointer' : 'default', margin: 0 },
            });
          },
        },
      hiCol.roTextColumn({ header: '부서', field: 'deptName', size: 110 }),
      hiCol.roTextColumn({ header: '사업', field: 'projectName', size: 120 }),
      hiCol.roTextColumn({ header: '과목', field: 'subjectName', size: 120 }),
      hiCol.roTextColumn({ header: '재원', field: 'fundName', size: 90 }),
      hiCol.roNumberColumn({ header: '예산액', field: 'budgetAmt', size: AMT_SIZE }),
      hiCol.edNumberColumn({
        header: '절감액',
        field: 'savingAmt',
        size: AMT_SIZE,
        editable: () => canEdit,
      }),
      hiCol.roNumberColumn({ header: '배분기준액', field: 'baseAmt', size: AMT_SIZE }),
      ...MONTH_STACK_COLUMNS.map((c) =>
        hiCol.stackNumberColumn({
          topField: c.top,
          bottomField: c.bottom,
          topLabel: c.topLabel,
          bottomLabel: c.bottomLabel,
          size: 86,
          topEditable: c.kind === 'month',
          bottomEditable: c.kind === 'month',
          editable: c.kind === 'month' ? () => canEdit : false,
        })
      ),
      hiCol.roNumberColumn({ header: '합계', field: 'total', size: 88 }),
      hiCol.roNumberColumn({ header: '미배분', field: 'unalloc', size: 88 }),
    ];
    },
    [canEdit]
  );

  useEffect(() => {
    const derivedFields = new Set([
      'baseAmt',
      'q1',
      'q2',
      'q3',
      'q4',
      'total',
      'unalloc',
      'budgetAmt',
      'hasUnalloc',
      '_sel',
    ]);
    grid.onAfterSaveCell = (rowData, { field }) => {
      if (!field || derivedFields.has(field)) return;
      // 스택 열 합성 id(m1_m7 등)로 저장되면 월 합산이 깨지므로 무시
      if (/^m\d+_m\d+$/i.test(String(field)) || /^q\d+_q\d+$/i.test(String(field))) return;
      const rowUid = rowData._rowUid || rowData.id;
      if (rowUid == null || String(rowUid).trim() === '') return;
      const enriched = enrichMonthlyRow({
        ...rowData,
        mod: rowData.mod === 'A' ? 'A' : 'U',
      });
      gridRef.current?.setData?.((prev) => {
        const next = (prev || []).map((r) => {
          if (String(r._rowUid || r.id || '') !== String(rowUid)) return r;
          return {
            ...enriched,
            _rowUid: rowUid,
            id: rowUid,
            _sel: r._sel || 'N',
          };
        });
        refreshWarn(next);
        return next;
      });
    };
  }, [grid, refreshWarn]);

  const applyDistribute = useCallback(
    (mode) => {
      if (!canEdit) return;
      // 셀 편집 중 blur 저장이 배분 결과를 덮어쓰지 않도록 편집 종료
      gridRef.current?.stopEditCell?.();
      const data = gridRef.current?.getData?.() || [];
      const isSelected = (r) => String(r?._sel || '').toUpperCase() === 'Y';
      const targets = data.filter(isSelected);
      if (!targets.length) {
        showAlert('자동계산할 행을 「선택」 체크하세요.', 'warning');
        return;
      }
      const modeLabel = { month: '월', quarter: '분기', half: '반기', year: '연' }[mode] || mode;
      const next = data.map((r) => {
        if (!isSelected(r)) return r;
        const rowUid = r._rowUid || r.id;
        const base = enrichMonthlyRow(r);
        const months = distributeMonthly(base.baseAmt, mode);
        // 기존 월·분기 잔여값을 남기지 않도록 월 필드를 먼저 비운 뒤 배분 결과 적용
        return enrichMonthlyRow({
          ...base,
          ...emptyMonthAmounts(),
          ...months,
          _rowUid: rowUid,
          id: rowUid,
          _sel: 'Y',
          mod: r.mod === 'A' ? 'A' : 'U',
        });
      });
      gridRef.current?.setData?.(next);
      // 전체 캐시에도 반영 (이후 조회/저장 시 배분 유실 방지)
      const byUid = new Map(
        next.map((r) => [String(r._rowUid || r.requestUid || r.id || ''), r])
      );
      allRowsRef.current = (allRowsRef.current || []).map((r) => {
        const id = String(r._rowUid || r.requestUid || r.id || '');
        return byUid.has(id) ? { ...byUid.get(id) } : r;
      });
      refreshWarn(next);
      const stillBad = next.filter(isSelected).filter((r) => enrichMonthlyRow(r).unalloc !== 0);
      if (stillBad.length) {
        showAlert(
          `선택 ${targets.length}건에 「${modeLabel}」 배분을 적용했지만, 미배분 ≠ 0 인 행이 ${stillBad.length}건 있습니다. 다시 시도하거나 해당 행을 확인하세요.`,
          'warning'
        );
      } else {
        showAlert(`선택 ${targets.length}건에 「${modeLabel}」 배분을 적용했습니다.`, 'success');
      }
    },
    [canEdit, refreshWarn]
  );

  /** 그리드에 입력·배분 후 아직 저장되지 않은 행이 있는지 */
  const hasUnsavedEdits = useCallback(() => {
    if (!canEdit) return false;
    const all = syncGridIntoAllRows();
    const dirtyRows = (all || []).some((r) => {
      const m = String(r?.mod || '').toUpperCase();
      return m === 'A' || m === 'U' || m === 'D';
    });
    if (dirtyRows) return true;
    // 셀 인라인 편집 중(아직 mod 미반영)도 미저장으로 간주
    const ae = document.activeElement;
    if (
      ae &&
      gridHostRef.current?.contains(ae) &&
      (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA') &&
      ae.type !== 'checkbox'
    ) {
      return true;
    }
    return false;
  }, [canEdit, syncGridIntoAllRows]);

  const doClose = useCallback(() => {
    setCloseConfirmOpen(false);
    onClose?.();
  }, [onClose]);

  const requestClose = useCallback(() => {
    if (closeConfirmOpen) return;
    if (hasUnsavedEdits()) {
      setCloseConfirmOpen(true);
      return;
    }
    doClose();
  }, [closeConfirmOpen, hasUnsavedEdits, doClose]);

  const handleSave = useCallback(async () => {
    if (!canEdit) return false;
    const snap = session?.dims;
    if (!snap?.budgetKindCode) {
      showAlert('예산차수(결합코드)가 없습니다.', 'warning');
      return false;
    }
    const all = syncGridIntoAllRows();
    const data = all.map((r) => {
      const rest = { ...r };
      delete rest._sel;
      delete rest.mod;
      return enrichMonthlyRow(rest);
    });
    const saved = await saveMonthlyBudgets(snap, data);
    const byReq = new Map(all.map((r) => [String(r.requestUid || ''), r]));
    const withSel = withMonthlyGridIds(
      saved.map((r) => {
        const prev = byReq.get(String(r.requestUid || ''));
        return {
          ...r,
          _rowUid: prev?._rowUid,
          _sel: 'N',
          mod: '',
        };
      })
    );
    allRowsRef.current = withSel;
    pushViewToGrid(withSel);
    const unallocCnt = withSel.filter((r) => r.hasUnalloc).length;
    showAlert(
      unallocCnt
        ? `월별예산을 저장했습니다. 미배분 행 ${unallocCnt}건 — 제출 전 배분을 완료하세요.`
        : '월별예산을 저장했습니다.',
      unallocCnt ? 'warning' : 'success'
    );
    return true;
  }, [canEdit, session, syncGridIntoAllRows, pushViewToGrid]);

  const handleSaveAndClose = useCallback(async () => {
    const ok = await handleSave();
    if (!ok) return;
    doClose();
  }, [handleSave, doClose]);

  // 미저장 확인 중 Esc → 취소(팝업 유지)
  useEffect(() => {
    if (!closeConfirmOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      setCloseConfirmOpen(false);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [closeConfirmOpen]);

  const openSearchLookup = useCallback((type) => {
    setLookup({ open: true, type });
  }, []);

  const clearSearchCodeFields = useCallback(
    (kind) => {
      if (kind === 'dept') {
        setValue('deptCode', '');
        setValue('deptName', '');
        setValue('deptDisp', '');
      } else if (kind === 'project') {
        setValue('projectCode', '');
        setValue('projectName', '');
        setValue('projectDisp', '');
      } else if (kind === 'subject') {
        setValue('subjectCode', '');
        setValue('subjectName', '');
        setValue('subjectDisp', '');
      } else if (kind === 'fund') {
        setValue('fundUid', '');
        setValue('fundPkgUid', '');
        setValue('fundCode', '');
        setValue('fundName', '');
        setValue('fundDisp', '');
      }
    },
    [setValue]
  );

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'acctClass',
          label: '회계구분',
          type: 'select',
          width: CommonCss.w110,
          labelWidth: SEARCH_LABEL_W,
          options: classOptions,
          title: '편성요구 검색조건과 동일 · 조회 시 반영',
        },
        {
          name: 'deptDisp',
          label: '부서',
          type: 'text',
          width: CommonCss.w160,
          labelWidth: SEARCH_LABEL_W,
          readonly: true,
          showSearch: true,
          searchTitle: '부서 조회',
          onSearch: () => openSearchLookup('dept'),
          placeholder: '전체',
        },
        {
          name: 'projectDisp',
          label: '사업',
          type: 'text',
          width: CommonCss.w180,
          labelWidth: SEARCH_LABEL_W,
          readonly: true,
          showSearch: true,
          searchTitle: '사업 조회',
          onSearch: () => openSearchLookup('project'),
          placeholder: '전체',
        },
        {
          name: 'subjectDisp',
          label: '과목',
          type: 'text',
          width: CommonCss.w180,
          labelWidth: SEARCH_LABEL_W,
          readonly: true,
          showSearch: true,
          searchTitle: '과목 조회',
          onSearch: () => openSearchLookup('subject'),
          placeholder: '전체',
        },
        {
          name: 'fundDisp',
          label: '재원',
          type: 'text',
          width: CommonCss.w160,
          labelWidth: SEARCH_LABEL_W,
          readonly: true,
          showSearch: true,
          searchTitle: '재원 조회',
          onSearch: () => openSearchLookup('fund'),
          placeholder: '전체',
        },
        {
          name: '_searchBtn',
          label: '',
          type: 'button',
          buttonLabel: '조회',
          icon: '🔍',
          onClick: handleSearchFilter,
          width: CommonCss.w70,
          labelWidth: '0px',
        },
      ],
    ],
    [classOptions, openSearchLookup, handleSearchFilter]
  );

  const lookupConfig = useMemo(() => {
    const snap = session?.dims || dims || {};
    const tenant = snap.tenantCode || tenantCode;
    if (lookup.type === 'dept') {
      const rows = filterDepts(loadDepts(tenant), { useYn: 'Y' }).map((r) => ({
        ...r,
        id: r.deptUid || r.deptCode,
        code: r.deptCode,
        name: r.deptName,
      }));
      return {
        title: '부서 조회 (월별예산)',
        rows,
        columns: [
          hiCol.roTextColumn({ header: '부서코드', field: 'deptCode', size: 110 }),
          hiCol.roTextColumn({ header: '부서명', field: 'deptName', size: 160 }),
        ],
        enableSearch: true,
        searchConfig: {
          mode: 'unified',
          keywordFields: ['deptCode', 'deptName', 'code', 'name'],
        },
      };
    }
    if (lookup.type === 'project') {
      const list = loadOrSeedProjects(snap) || [];
      const rows = list
        .filter((r) => r.useYn !== 'N')
        .map((r) => ({
          ...r,
          mod: '',
          id: r.projectUid || r.projectCode,
          code: r.projectCode,
          name: r.projectName,
        }));
      return {
        title: '사업 조회 (월별예산)',
        rows,
        columns: [
          hiCol.roTextColumn({ header: '사업코드', field: 'projectCode', size: 110 }),
          hiCol.roTextColumn({ header: '사업명', field: 'projectName', size: 180 }),
        ],
        enableSearch: true,
        searchConfig: {
          mode: 'unified',
          keywordFields: ['projectCode', 'projectName', 'code', 'name'],
        },
      };
    }
    if (lookup.type === 'subject') {
      const all = loadSubjectsByFilter({
        ...snap,
        inoutDiv: snap.inoutDiv || dims?.inoutDiv || '지출',
      });
      const finals = (all || []).filter((r) => r.finalYn === 'Y' && r.useYn !== 'N' && r.mod !== 'D');
      const rows = finals.map((r) => {
        const disp = buildSubjectDispCode(r, all);
        const dispName = buildSubjectDispName(r, all);
        return {
          ...r,
          id: r.subjectUid || `${r.budgetType}-${r.subjectCode}`,
          code: disp,
          name: dispName,
          subjectDispCode: disp,
          subjectDispName: dispName,
          subjectName: dispName,
        };
      });
      return {
        title: '과목 조회 (월별예산)',
        rows,
        columns: [
          hiCol.roTextColumn({ header: '결합코드', field: 'subjectDispCode', size: 140 }),
          hiCol.roTextColumn({ header: '과목명', field: 'subjectDispName', size: 260 }),
        ],
        enableSearch: true,
        searchConfig: {
          mode: 'unified',
          keywordFields: ['subjectDispCode', 'subjectCode', 'subjectDispName', 'subjectName', 'code', 'name'],
        },
      };
    }
    if (lookup.type === 'fund') {
      const fundRows = loadFunds(snap)
        .filter((r) => r.useYn !== 'N')
        .map((r) => ({
          ...r,
          id: r.fundUid || r.fundCode,
          code: r.fundCode,
          name: r.fundName,
          _kind: 'fund',
        }));
      const fundCols = [
        hiCol.roTextColumn({ header: '재원코드', field: 'fundCode', size: 100 }),
        hiCol.roTextColumn({ header: '재원명', field: 'fundName', size: 140 }),
      ];
      const pkgRows = loadFundPackages(snap)
        .filter((r) => r.useYn !== 'N')
        .map((r) => ({
          ...r,
          id: r.fundPkgUid || r.pkgCode,
          code: r.pkgCode,
          name: r.pkgName,
          _kind: 'package',
        }));
      return {
        title: '재원·재원구성 조회 (월별예산)',
        rows: fundRows,
        columns: fundCols,
        tabs: [
          { id: 'fund', label: '재원', rows: fundRows, columns: fundCols },
          {
            id: 'package',
            label: '재원구성',
            rows: pkgRows,
            columns: [
              hiCol.roTextColumn({ header: '구성코드', field: 'pkgCode', size: 110 }),
              hiCol.roTextColumn({ header: '구성명', field: 'pkgName', size: 160 }),
            ],
          },
        ],
        enableSearch: true,
        searchConfig: {
          mode: 'unified',
          keywordFields: ['fundCode', 'fundName', 'pkgCode', 'pkgName', 'code', 'name'],
        },
      };
    }
    return { title: '코드 조회', rows: [], columns: [] };
  }, [lookup.type, session?.dims, dims, tenantCode]);

  const applyLookup = useCallback(
    (payload) => {
      const rows = (Array.isArray(payload) ? payload : [payload]).filter(Boolean);
      if (!rows.length) {
        if (lookup.type === 'dept') clearSearchCodeFields('dept');
        else if (lookup.type === 'project') clearSearchCodeFields('project');
        else if (lookup.type === 'subject') clearSearchCodeFields('subject');
        else if (lookup.type === 'fund') clearSearchCodeFields('fund');
        setLookup({ open: false, type: null });
        return;
      }
      if (lookup.type === 'dept') {
        const items = rows
          .map((r) => ({
            code: String(r.deptCode || '').trim(),
            name: String(r.deptName || '').trim(),
          }))
          .filter((x) => x.code);
        setValue('deptCode', joinCodes(items.map((x) => x.code)));
        setValue('deptName', items.map((x) => x.name).filter(Boolean).join(', '));
        setValue('deptDisp', formatMultiCodeName(items));
      } else if (lookup.type === 'project') {
        const items = rows
          .map((r) => ({
            code: String(r.projectCode || '').trim(),
            name: String(r.projectName || '').trim(),
          }))
          .filter((x) => x.code);
        setValue('projectCode', joinCodes(items.map((x) => x.code)));
        setValue('projectName', items.map((x) => x.name).filter(Boolean).join(', '));
        setValue('projectDisp', formatMultiCodeName(items));
      } else if (lookup.type === 'subject') {
        const snap = session?.dims || dims || {};
        const all = loadSubjectsByFilter({
        ...snap,
        inoutDiv: snap.inoutDiv || dims?.inoutDiv || '지출',
      });
        const items = rows
          .map((r) => {
            const code =
              r.subjectDispCode || buildSubjectDispCode(r, all) || r.subjectCode || '';
            const name =
              r.subjectDispName || r.subjectName || buildSubjectDispName(r, all) || '';
            const leaf = String(r.subjectCode || code).trim();
            return { code: String(code).trim(), name: String(name).trim(), leaf };
          })
          .filter((x) => x.code);
        setValue('subjectCode', joinCodes(items.flatMap((x) => [x.leaf, x.code].filter(Boolean))));
        setValue('subjectName', items.map((x) => x.name).filter(Boolean).join(', '));
        setValue('subjectDisp', formatMultiCodeName(items));
      } else if (lookup.type === 'fund') {
        const fundItems = [];
        const pkgItems = [];
        for (const r of rows) {
          const isPkg = r._lookupTab === 'package' || r._kind === 'package' || r.fundPkgUid;
          if (isPkg && (r.fundPkgUid || r.pkgCode)) {
            const code = String(r.pkgCode || '').trim();
            if (!code) continue;
            pkgItems.push({
              uid: String(r.fundPkgUid || '').trim(),
              code,
              name: String(r.pkgName || '').trim(),
            });
          } else {
            const code = String(r.fundCode || '').trim();
            if (!code) continue;
            fundItems.push({
              uid: String(r.fundUid || '').trim(),
              code,
              name: String(r.fundName || '').trim(),
            });
          }
        }
        setValue('fundUid', joinCodes(fundItems.flatMap((x) => [x.uid, x.code].filter(Boolean))));
        setValue('fundPkgUid', joinCodes(pkgItems.flatMap((x) => [x.uid, x.code].filter(Boolean))));
        const dispItems = [
          ...fundItems.map((x) => ({ code: x.code, name: x.name })),
          ...pkgItems.map((x) => ({ code: x.code, name: x.name })),
        ];
        setValue('fundCode', joinCodes(dispItems.map((x) => x.code)));
        setValue('fundName', dispItems.map((x) => x.name).filter(Boolean).join(', '));
        setValue('fundDisp', formatMultiCodeName(dispItems));
      }
      setLookup({ open: false, type: null });
    },
    [lookup.type, clearSearchCodeFields, setValue, session?.dims, dims]
  );

  if (!open) return null;

  const titleAcct = acctClassLabel(
    getValues('acctClass') || session?.dims?.acctClass || dims?.acctClass,
    tenantCode
  );

  return (
    <DraggableDialog
      open={open}
      title={`${MONTHLY_BUDGET_LABEL} · ${session?.dims?.fiscalYear || dims?.fiscalYear || ''} · ${
        session?.dims?.budgetKindCode || dims?.budgetKindCode || ''
      }${titleAcct ? ` · ${titleAcct}` : ''}`}
      onClose={requestClose}
      escapeEnabled={!closeConfirmOpen}
      dialogWidth="min(96vw, 1400px)"
      dialogHeight="min(92vh, 820px)"
      keepMounted={false}
    >
      <Body>
        <Toolbar>
          <ActionButton type="button" onClick={() => applyDistribute('month')} disabled={!canEdit}>
            월
          </ActionButton>
          <ActionButton type="button" onClick={() => applyDistribute('quarter')} disabled={!canEdit}>
            분기
          </ActionButton>
          <ActionButton type="button" onClick={() => applyDistribute('half')} disabled={!canEdit}>
            반기
          </ActionButton>
          <ActionButton type="button" onClick={() => applyDistribute('year')} disabled={!canEdit}>
            연
          </ActionButton>
          <Hint>
            체크한 행만 일괄 배분 · 표시 {viewCount}건 / 전체 {allRowsRef.current?.length || 0}건
            {warnMsg ? ` · ${warnMsg}` : ''}
          </Hint>
        </Toolbar>

        <SearchWrap>
          <FormProvider {...methods}>
            <FormArea
              fields={searchFields}
              compact
              onFieldEnter={handleSearchFilter}
              defaultValues={searchDefaults}
            />
          </FormProvider>
        </SearchWrap>

        <GridHost ref={gridHostRef}>
          <HiGridComponent
            ref={gridRef}
            columns={columns}
            height="100%"
            showRowNumber
            rowNumberSize={56}
            showModColumn
            enableRowCheck={false}
            rowHeight={52}
            useVirtualRows={false}
            {...grid.contextMenuProps}
            enableRowEditMenu={false}
            enableHeaderMenu
            enableColumnFilters
            enableFreeze
            enableSelectionStats
            enableQuickExport
            exportFileName={MONTHLY_BUDGET_LABEL}
            gridId={GRID_ID}
            prefsUserId={prefsUserId}
            colOptions={grid.colOptions}
          />
        </GridHost>

        <FooterBar>
          <ActionButton type="button" onClick={requestClose}>
            닫기
          </ActionButton>
          {canEdit && (
            <ActionButton type="button" $variant="success" onClick={handleSave}>
              저장
            </ActionButton>
          )}
        </FooterBar>
      </Body>

      <CodeLookupDialog
        open={lookup.open}
        title={lookupConfig.title}
        columns={lookupConfig.columns}
        rows={lookupConfig.rows}
        tabs={lookupConfig.tabs}
        enableSearch={!!lookupConfig.enableSearch}
        searchConfig={lookupConfig.searchConfig}
        multiSelect
        prefsUserId={prefsUserId}
        sizeKey={lookup.type ? `codeLookup-${lookup.type}` : 'codeLookup'}
        onSelect={applyLookup}
        onClose={() => setLookup({ open: false, type: null })}
      />

      {closeConfirmOpen && (
        <CloseConfirmOverlay
          role="dialog"
          aria-modal="true"
          aria-label="미저장 닫기 확인"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <CloseConfirmBox>
            <CloseConfirmMsg>
              입력중인 월별예산 데이터를 저장하지 않고 창을 닫으시겠습니까?
            </CloseConfirmMsg>
            <CloseConfirmBtns>
              <ActionButton type="button" $variant="success" onClick={handleSaveAndClose}>
                저장 후 닫기
              </ActionButton>
              <ActionButton type="button" onClick={doClose}>
                닫기
              </ActionButton>
              <ActionButton type="button" onClick={() => setCloseConfirmOpen(false)}>
                취소
              </ActionButton>
            </CloseConfirmBtns>
          </CloseConfirmBox>
        </CloseConfirmOverlay>
      )}
    </DraggableDialog>
  );
}
